<script lang="ts" setup>
import { ref, watch } from 'vue'
import { breakpointsTailwind, useBreakpoints } from '@vueuse/core'
import VideoFrame from '@/components/product-shared/VideoFrame'
import HeroEditor from './HeroEditor.vue'

const frontVideo = ref<'browser' | 'editor'>('editor')

const { md } = useBreakpoints(breakpointsTailwind)

const $video = ref<HTMLVideoElement>()

function playVideo() {
  const video = $video.value
  if (video) {
    const isPlaying =
      video.currentTime > 0 && !video.paused && !video.ended && video.readyState > video.HAVE_CURRENT_DATA
    if (!isPlaying) {
      video.play()
    }
  }
}

watch(frontVideo, (val) => {
  if (val === 'browser') {
    playVideo()
  } else if ($video.value) {
    const video = $video.value
    const isPlaying =
      video.currentTime > 0 && !video.paused && !video.ended && video.readyState > video.HAVE_CURRENT_DATA
    if (isPlaying) {
      video.pause()
      video.currentTime = 0
    }
  }
})

function editorCallback() {
  setTimeout(() => {
    frontVideo.value = 'browser'
    playVideo()
  }, 1200)
}

const SSR = import.meta.env.SSR
const controls = ref(false)
</script>

<template>
  <div data-cy="homepage-hero-video" class="relative sm:m-[20px] md:m-[40px]" style="aspect-ratio: 16/9">
    <VideoFrame
      :size="!SSR && md ? 'lg' : 'sm'"
      frame="browser"
      :window-urls="['http://localhost:3000']"
      class="absolute w-full transform shadow-asset transition duration-500"
      :class="{
        'translate-y-[-16px] scale-[.95] opacity-[.85] hover:translate-y-[-52px] hover:duration-300 md:translate-y-[-40px]':
          frontVideo !== 'browser',
        'z-10': frontVideo === 'browser',
      }"
      @click="frontVideo = 'browser'"
    >
      <video
        ref="$video"
        class="block w-full rounded-b-lg"
        muted
        playsinline
        preload="auto"
        :controls="controls"
        @mouseover="controls = true"
        @mouseout="controls = false"
      >
        <source src="/videos/home_page.webm" type="video/webm" />
        <source src="/videos/home_page.mp4" type="video/mp4" />
        <track src="/videos/no-audio.vtt" kind="captions" srclang="en" label="English captions" default />
      </video>
    </VideoFrame>
    <VideoFrame
      size="lg"
      frame="editor"
      :window-urls="['Authentication.cy.ts', 'Button.tsx', 'Button.cy.tsx']"
      class="absolute h-[210px] w-full transform text-[12px] leading-[16px] shadow-asset transition duration-500 sm:h-auto md:text-[16px] md:leading-[20px]"
      :class="{
        'translate-y-[-24px] scale-[.95] opacity-[.85] sm:translate-y-[-16px] md:translate-y-[-40px] md:hover:translate-y-[-56px] md:hover:duration-300':
          frontVideo !== 'editor',
        'z-10': frontVideo === 'editor',
      }"
      @click="frontVideo = 'editor'"
    >
      <HeroEditor @done="editorCallback" />
    </VideoFrame>
  </div>
</template>
